import { __read, __assign } from 'tslib';
import 'intersection-observer';
import * as React from 'react';
import { useInView } from 'react-intersection-observer';
import { createContext } from '../common/createContext.esm.js';
import { useAnalyticsQueue as useAnalyticsQueue$1 } from '@invitae/analytics-queue';

var _a, _b, _c;
var usePageAnalyticsContext = (_b = __read(createContext((_a = {
        currentPageUrl: 'default',
        debug: true,
        previousPageUrl: 'default'
    },
    _a['Site location'] = 'default',
    _a)), 2), _b[0]), PageAnalyticsContextProvider = _b[1];
var useSectionAnalyticsContext = (_c = __read(createContext({
    section: 'default',
}), 2), _c[0]), SectionAnalyticsContextProvider = _c[1];
/**
 * returns a logEvent and linkClickLogEvent functions to store analytics data. The function will automatically attach page and section
 * information to the event if inside a context
 */
var useAnalyticsQueue = function (_a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.debug, debug = _c === void 0 ? false : _c;
    // section analytics is not always present
    var sectionAnalyticsProps = useSectionAnalyticsContext({ noThrow: true });
    var _d = usePageAnalyticsContext({ noThrow: true }) || {}, currentPageUrl = _d.currentPageUrl, previousPageUrl = _d.previousPageUrl, siteLocation = _d["Site location"];
    var _e = useAnalyticsQueue$1({ debug: debug }), logEventInternal = _e.logEvent, setUserProperties = _e.setUserProperties;
    var logEvent = React.useCallback(function (_a) {
        var eventName = _a.eventName, eventProperties = _a.eventProperties;
        var props = __assign(__assign({}, eventProperties), (sectionAnalyticsProps || {}));
        if (siteLocation)
            props['Site location'] = siteLocation;
        if (currentPageUrl) {
            props['Current page'] = currentPageUrl;
        }
        if (previousPageUrl) {
            props['Previous page'] = previousPageUrl;
        }
        logEventInternal({
            eventName: eventName,
            eventProperties: props,
        });
    }, [logEventInternal, sectionAnalyticsProps, previousPageUrl, currentPageUrl, siteLocation]);
    var linkClickLogEvent = function (_a) {
        var currentPage = _a.currentPage, eventProperties = _a.eventProperties, linkName = _a.linkName;
        var eventName = "click to ".concat(linkName, " on ").concat(currentPage);
        logEvent({
            eventName: eventName,
            eventProperties: eventProperties,
        });
    };
    return { linkClickLogEvent: linkClickLogEvent, logEvent: logEvent, setUserProperties: setUserProperties };
};
var useAnalyticsEvents = function (_a) {
    var analyticsEvents = _a.analyticsEvents, defaultOnClick = _a.defaultOnClick;
    var logEvent = useAnalyticsQueue().logEvent;
    var onClick = React.useCallback(function (event) {
        var href = event.currentTarget.getAttribute('href');
        if (!analyticsEvents)
            return defaultOnClick ? defaultOnClick() : true;
        var onClickEvents = analyticsEvents.filter(function (e) { var _a; return ((_a = e.fields) === null || _a === void 0 ? void 0 : _a.type) === 'click'; });
        onClickEvents.forEach(function (e) {
            var _a;
            logEvent({
                eventName: ((_a = e.fields) === null || _a === void 0 ? void 0 : _a.name) || '',
                eventProperties: {
                    'Link destination': href,
                },
            });
        });
        if (defaultOnClick) {
            defaultOnClick();
        }
        return true;
    }, [analyticsEvents, defaultOnClick, logEvent]);
    var _b = __read(React.useState(null), 2), hoverDate = _b[0], setHoverDate = _b[1];
    var onMouseEnter = React.useCallback(function () {
        setHoverDate(new Date());
    }, []);
    var onMouseLeave = React.useCallback(function () {
        if (!hoverDate || !analyticsEvents)
            return;
        var onHoverEvents = analyticsEvents.filter(function (e) { var _a; return ((_a = e.fields) === null || _a === void 0 ? void 0 : _a.type) === 'hover'; });
        var timeDiff = new Date().getTime() - hoverDate.getTime(); //in ms
        onHoverEvents.forEach(function (entry) {
            var _a;
            logEvent({
                eventName: (_a = entry.fields) === null || _a === void 0 ? void 0 : _a.name,
                eventProperties: {
                    'Duration in seconds': Math.round(timeDiff / 1000),
                },
            });
        });
        setHoverDate(null);
    }, [hoverDate, analyticsEvents, logEvent]);
    var _c = __read(useInView({ threshold: 0.6 }), 2), ref = _c[0], inView = _c[1];
    React.useEffect(function () {
        if (!analyticsEvents)
            return;
        if (inView) {
            analyticsEvents
                .filter(function (entry) { var _a; return ((_a = entry.fields) === null || _a === void 0 ? void 0 : _a.type) === 'appear'; })
                .forEach(function (entry) {
                var _a;
                logEvent({
                    eventName: ((_a = entry.fields) === null || _a === void 0 ? void 0 : _a.name) || '',
                    eventProperties: {},
                });
            });
        }
        else {
            analyticsEvents
                .filter(function (entry) { var _a; return ((_a = entry.fields) === null || _a === void 0 ? void 0 : _a.type) === 'disappear'; })
                .forEach(function (entry) {
                var _a;
                logEvent({
                    eventName: ((_a = entry.fields) === null || _a === void 0 ? void 0 : _a.name) || '',
                    eventProperties: {},
                });
            });
        }
    }, [inView, analyticsEvents, logEvent]);
    return { onClick: onClick, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, ref: ref };
};
/* eslint-enable @typescript-eslint/naming-convention */

export { PageAnalyticsContextProvider, SectionAnalyticsContextProvider, useAnalyticsEvents, useAnalyticsQueue, usePageAnalyticsContext, useSectionAnalyticsContext };
//# sourceMappingURL=useAnalytics.hook.esm.js.map
